/** Minimal translate signature (key + English fallback, interpolation values), structurally
 *  satisfied by i18next's `t`. */
export type TranslateFn = (
  key: string,
  fallback: string,
  values?: Record<string, unknown>
) => string;

export const truncateString = (str: string, maxLength: number) => {
  return str.length > maxLength ? str.substring(0, maxLength) + "..." : str;
};

/** "50 Free Spins in Book of Dead" / "… in 3 games" / "… in all games" / "… - All games from: X"
 *  → "50 Free Spins". Everything after the count is game information, which renders as chips
 *  where there is room and is dropped where there isn't (the leaderboard). */
export function stripFreespinGameName(title: string): string {
  const match = title.trim().match(/^(\d+\s+free\s+spins?)\b/i);
  return match ? match[1] : title;
}

const oneDecimal = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 1,
});
const COMPACT_STEPS = [
  [1e9, "B"],
  [1e6, "M"],
  [1e3, "k"],
] as const;

/** A score's compact form, for the button's tournament cells: "5.1k",
 *  "123.4M" — the same letters in every language, with the language's own
 *  decimal mark ("5,1k"). Not the browser's compact notation, which has no
 *  short thousands in German ("5100") and writes "5,1 mil" in Spanish
 *  (Sebastian, 2026-10-26: "isn't 'k' a global understanding of 1000?";
 *  "lowercase k"); the millions stay "M" — "m" would read as milli.
 *  Cut, never rounded: the short form never shows a point more than the
 *  player has — 5,199 is "5.1k", 999,960 is "999.9k", 950 stays "950"
 *  (Sebastian: "we will never round the point value ever"). */
export function formatCompactScore(n: number): string {
  const abs = Math.abs(n);
  const at = COMPACT_STEPS.findIndex(([step]) => abs >= step);
  if (at < 0) return n.toLocaleString();
  const [step, letter] = COMPACT_STEPS[at];
  // In whole tenths of the step (exact for whole scores), cut toward zero.
  const tenths = Math.floor(abs / (step / 10)) / 10;
  return `${n < 0 ? "-" : ""}${oneDecimal.format(tenths)}${letter}`;
}

/**
 * Locale-tolerant parser for backend-rendered display numbers (progress
 * values, money amounts inside prize titles). Plain/scientific decimals parse
 * directly; otherwise symbols are stripped and the RIGHTMOST separator is the
 * decimal point — covers "1,060.50" (en) and "1.060,50" (eu) alike.
 * Best-effort; null when it doesn't look numeric.
 */
export function parseDisplayNumber(
  raw: string | null | undefined
): number | null {
  if (raw == null) return null;
  const trimmed = raw.trim();
  // Plain/scientific decimal — parse before the symbol-stripping mangles "E".
  if (/^-?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(trimmed)) {
    const direct = Number.parseFloat(trimmed);
    return Number.isFinite(direct) ? direct : null;
  }
  let s = trimmed.replace(/[^\d.,-]/g, "");
  if (!s) return null;
  const lastDot = s.lastIndexOf(".");
  const lastComma = s.lastIndexOf(",");
  if (lastDot !== -1 && lastComma !== -1) {
    s =
      lastComma > lastDot
        ? s.replace(/\./g, "").replace(",", ".")
        : s.replace(/,/g, "");
  } else if (lastComma !== -1) {
    // ",dd" tail = decimal comma ("25,00"); otherwise thousands ("1,060").
    s = /,\d{1,2}$/.test(s) ? s.replace(",", ".") : s.replace(/,/g, "");
  }
  const n = Number.parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

// A currency token, matching the closed set of shapes the backend's money
// formatter actually emits (common/currency.py + exchange.py) rather than any
// short word — catalog-item prize titles are unconstrained admin free text,
// so "10 Tokens" must NOT read as money:
//   - a token containing a currency symbol: "€", "$", "R$"
//   - an all-caps code: "EUR", "USDT", "SC", "GC"
//   - "kr" (the one lowercase custom format, NOK/SEK)
//   - milli/micro sub-currency symbols: "mLTC", "μBTC"
const CUR = `(?:${[
  "[\\p{L}\\p{Sc}]{0,4}\\p{Sc}[\\p{L}\\p{Sc}]{0,4}",
  "[A-Z]{1,5}",
  "kr",
  "[mμ][A-Z]{2,4}",
].join("|")})`;
// An amount: digits with grouping/decimal separators ("1,500.50", "1 060,50").
const AMOUNT = "\\d[\\d\\s.,'’]*";
// A title that is NOTHING BUT money. Cash is the only prize type the backend
// titles as a bare amount ("€100", "USDT 10", "100 kr") — bonus titles carry a
// word ("€100 bonus"), free spins a phrase ("25 Free Spins") — so this shape
// is the cash discriminator available to the FE (the wire carries no kind).
const CASH_TITLE_RE = new RegExp(
  `^(?:${CUR}\\s?${AMOUNT}|${AMOUNT}\\s?${CUR})$`,
  "u"
);

export const isCashPrizeTitle = (title: string): boolean =>
  CASH_TITLE_RE.test(title.trim());

/** A cash prize as `formatPrizeTitle` renders it: the bare amount, or the amount plus the
 *  translated "Cash" suffix (`cashSuffix`). The display-side twin of `isCashPrizeTitle`. */
export const isFormattedCashPrizeTitle = (
  label: string,
  cashSuffix: string
): boolean => {
  const trimmed = label.trim();
  if (isCashPrizeTitle(trimmed)) return true;
  const suffix = ` ${cashSuffix}`;
  return (
    trimmed.endsWith(suffix) &&
    isCashPrizeTitle(trimmed.slice(0, -suffix.length))
  );
};

/** A bonus prize: an amount followed by the bonus word ("€10 Bonus", "€100 bonus") — the
 *  backend's bonus title shape, in English or the translated `prizes.bonus` word. Money all the
 *  same, so it wears the currency mark like cash rather than the star. */
export const isFormattedBonusPrizeTitle = (
  label: string,
  bonusWord: string
): boolean => {
  const match = label.trim().match(/^(.*\S)\s+(\S+)$/u);
  if (!match) return false;
  const word = match[2].toLocaleLowerCase();
  if (word !== "bonus" && word !== bonusWord.toLocaleLowerCase()) return false;
  return isCashPrizeTitle(match[1]);
};

/**
 * Comparable money value of a prize title, for value-ranking (missions "Top
 * prize"). Money-shaped ("€1,500") and money-prefixed ("€100 bonus") titles
 * yield their amount; unpriceable ones (free spins, catalog items) yield null
 * and rank below priced ones. Titles all render in the one display currency,
 * so amounts are comparable without conversion.
 */
export function prizeMoneyValue(title: string): number | null {
  const trimmed = title.trim();
  const prefixed = trimmed.match(new RegExp(`^${CUR}\\s?(${AMOUNT})`, "u"));
  if (prefixed) return parseDisplayNumber(prefixed[1]);
  if (!CASH_TITLE_RE.test(trimmed)) return null;
  const amount = trimmed.match(new RegExp(`(${AMOUNT})`, "u"));
  return amount ? parseDisplayNumber(amount[1]) : null;
}

/** Every wording the backend titles free spins with, in the player's
 *  language — the msgstrs of `"{} Free Spins"` and its "in {games}" variants
 *  in `backend/locale/*\/LC_MESSAGES/django.po` (some languages word the
 *  variants differently, and a few still carry the English). Titles arrive
 *  translated and nothing on the wire says a prize is free spins, so the
 *  button finds them by these words (Sebastian, 2026-10-21: "in German
 *  prizes use 'Freispiele' — for the button we always label free spins as
 *  FS"). Add a wording here when the backend's changes. */
const FREE_SPINS_WORDS = [
  "free spins?",
  "freispiele",
  "δωρεάν περιστροφές",
  "giros gratis",
  // The backend's Spanish typo, fixed in the same change: kept one release so
  // a title cached before the backend deploy still reads "FS" (👺 Belmont, B5).
  "giros gatis",
  "ilmaiskierrosta",
  "besplatnih vrtnji",
  "ingyenes pörgetések",
  "giri gratuiti",
  "フリースピン",
  "უფასო სპინი",
  "тегін айналдыру",
  "тегін айналым",
  "gratisspinn",
  "darmowe spiny",
  "giros grátis",
  "rodadas grátis",
  "фриспины",
  "бесплатные вращения",
  "besplatnih spinova",
  "besplatna okretaja",
  "besplatne okretaje",
  "ücretsiz dönüş",
  "vòng quay miễn phí",
  "次?免费旋转",
  "次?免費旋轉",
].map((word) => word.replace(/ /g, "\\s+"));
/** A free-spins title: its count first (Japanese may lead with "全ゲームで"),
 *  then the wording, the count glued to it in CJK. */
const FREE_SPINS_RE = new RegExp(
  `^(?:全ゲームで\\s*)?(\\d+)\\s*(?:${FREE_SPINS_WORDS.join("|")})(?![\\p{L}])`,
  "iu"
);

/** Spin count of a free-spins prize title in any language the backend
 *  writes ("50 Free Spins in …", "50 Freispiele", "50 次免費旋轉" → 50); null
 *  for anything else. */
export function freeSpinCount(title: string): number | null {
  const match = title.trim().match(FREE_SPINS_RE);
  return match ? Number.parseInt(match[1], 10) : null;
}

/**
 * Highest-value prize. Ranking is tiered:
 *   1. the wire `value` (backend `max_cost` in display currency — the only
 *      signal that can price a free-text catalog item). 0 means "unpriced"
 *      (some free-spin classes report a zero max_cost), so only > 0 counts;
 *   2. money parsed from the title (older cached payloads without `value`);
 *   3. free-spin count (money and spins aren't comparable, so any priced
 *      prize beats any counted one);
 *   4. the first prize in the given order.
 */
export function topPrizeByTitle<
  T extends { title: string; value?: number | null },
>(prizes: T[]): T | null {
  if (prizes.length === 0) return null;
  const maxBy = (value: (p: T) => number | null) => {
    let best: T | null = null;
    let bestValue = -Infinity;
    for (const prize of prizes) {
      const v = value(prize);
      if (v !== null && v > bestValue) {
        best = prize;
        bestValue = v;
      }
    }
    return best;
  };
  return (
    maxBy((p) => (p.value != null && p.value > 0 ? p.value : null)) ??
    maxBy((p) => prizeMoneyValue(p.title)) ??
    maxBy((p) => freeSpinCount(p.title)) ??
    prizes[0]
  );
}

/**
 * Display form of a backend prize title: free-spins titles lose their embedded
 * game name (games render as separate chips), and cash prizes — the only type
 * whose title is a bare amount — get the localized "Cash" written out so the
 * player can tell WHAT "€100" is (QA feedback; the legacy overlay did the
 * same client-side).
 */
export const formatPrizeTitle = (title: string, t: TranslateFn): string => {
  const stripped = stripFreespinGameName(title);
  return isCashPrizeTitle(stripped)
    ? `${stripped} ${t("prizes.cash", "Cash")}`
    : stripped;
};

/**
 * A prize title on the provider (in-game) button, where room is tight
 * (Sebastian, 2026-10-16: "this prize format is for the provider button only —
 * we still want Free Spins when opening the campaign dashboard, where we have
 * plenty of space"): free spins short (`prizes.freespinsShort`, "50 FS" —
 * kept as "FS" in every language, a partner can retranslate it), a cash
 * amount bare ("€100", no "Cash"); anything else as `formatPrizeTitle`
 * without its cash word. Free spins are found in every language the backend
 * titles them in (`FREE_SPINS_WORDS`). The button speaks the short form in
 * full (`speakShortFreeSpins`).
 */
export const formatButtonPrizeTitle = (
  title: string,
  t: TranslateFn
): string => {
  const stripped = stripFreespinGameName(title);
  const spins = freeSpinCount(stripped);
  return spins !== null
    ? t("prizes.freespinsShort", "{{count}} FS", { count: spins })
    : stripped;
};

/** A text with the button's short free spins (`formatButtonPrizeTitle`) said
 *  in full, for a screen reader (which spells "FS" out as letters): the
 *  pattern is the short key's own, so the shown and spoken forms can't drift. */
export const speakShortFreeSpins = (text: string, t: TranslateFn): string => {
  const [before, after] = t("prizes.freespinsShort", "{{count}} FS", {
    count: "\u0000",
  }).split("\u0000");
  if (after === undefined) return text;
  const escape = (part: string) => part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const short = new RegExp(`${escape(before)}(\\d+)${escape(after)}`, "g");
  return text.replace(
    short,
    (_, count: string) => `${count} ${t("prizes.freespins", "Free Spins")}`
  );
};
